import { types, getEnv, getSnapshot, flow as flow$1 } from 'mobx-state-tree';
import { flow, values } from 'mobx';
import parseISO from 'date-fns/parseISO';
import isValid from 'date-fns/isValid';
import lightFormat from 'date-fns/lightFormat';
import md5 from 'md5';
import LRU from 'lru-cache';

// import { flow } from 'mobx';

/*
 *	Collection
 *
 *	A model for a collection of items
 */

const Collection = types.model(`Collection`, {})
// allow collections to be created with blank arrays or undefined
.preProcessSnapshot(snapshot => {
  if (snapshot === undefined || Array.isArray(snapshot)) {
    return {
      collection: snapshot || []
    };
  }
  return snapshot;
}).views(self => ({
  get isEmpty() {
    return self.length === 0;
  },
  get length() {
    return self.collection.length;
  }
})).actions(self => ({
  setItems(collection = []) {
    self.collection = collection;
  },
  addItem(item) {
    self.collection.push(item);
  },
  /**
   * Add multiple items to the end of the collection
   * @param {*} items
   */
  addItems(items) {
    self.collection = [...self.collection, ...items];
  },
  prependItem(item) {
    self.collection.unshift(item);
  },
  /**
   * Add multiple items to the beginning of the collection
   * @param {*} items
   */
  prependItems(items) {
    self.collection = [...items, ...self.collection];
  },
  dequeueItem() {
    self.collection.shift();
  },
  popItem() {
    self.collection.pop();
  },
  clearItems() {
    self.collection.clear();
  },
  filterItems(comparator) {
    return self.collection.filter(comparator);
  },
  findItem(prop, val) {
    return self.collection.find(item => prop ? item[prop] === val : item === val);
  },
  deleteItem(key, value) {
    const toRemove = self.findItem(key, value);
    self.collection.remove(toRemove);
  },
  map(mapper) {
    return self.collection.map(mapper);
  }
}));
const createCollection = (name, arrayOfModel) => types.compose(Collection, types.model(`${name}Collection`, {
  collection: types.array(arrayOfModel)
}));

/*
 *	CreateAsyncFlow
 *
 *	creates a model for handling asynchronous operations
 *	automatically sets loading the flag if there is an operation in progress
 */

const CreateAsyncActionsFlow = actionsObj => types.model('AsyncFlow', {})
// the loading flag doesn't need to be persisted
.volatile(self => ({
  loading: false,
  __defaultFn: res => res,
  __beforeResponseSuccess: res => res,
  __afterResponseSuccess: res => res,
  __beforeError: res => res,
  __afterError: res => res,
  __finally: res => res,
  __onSuccess: res => res,
  __onError: res => res
})).actions(self => {
  const mstActions = Object.keys(actionsObj).reduce((actions, actionName) => ({
    ...actions,
    // the interface that devs will call to start an async action
    [actionName]: function (...args) {
      return self[`__${actionName}`](...args);
    },
    // an async action that will get passed the args
    [`__${actionName}`]: flow(function* (...args) {
      try {
        self.setLoading(true);
        // curry the action function with self
        // give args as well in case devs want to do something extra with them
        // not sure why they would need to though
        const asyncAction = actionsObj[actionName].action(self, ...args);

        // call the curried async action with any passed args and self
        // this means that if no params are passed, only self will be passed as a param
        let response = yield asyncAction(...args, self);
        response = yield self.__beforeResponseSuccess(response, self);
        yield actionsObj[actionName].onSuccess(self)(response);
        yield self.__afterResponseSuccess(response, self);
      } catch (e) {
        console.error(e);
        const error = yield self.__beforeError(e, ...args, self);
        yield actionsObj[actionName].onError(self)(error);
        yield self.__afterError(e, ...args, self);
      } finally {
        // stop the loading no matter what
        self.setLoading(false);
        yield self.__finally(self);
      }
    })
  }), {});
  return {
    ...mstActions,
    setLoading(isLoading) {
      // need to do this because when we generate actions like this,
      // they become out of context of the actions section of the model
      // which prevents us from mutating data.  In MST, all data must be
      // mutated through actions
      self.loading = isLoading;
    },
    beforeResponseSuccess(beforeResponseSuccessFn) {
      self.__beforeResponseSuccess = beforeResponseSuccessFn;
    },
    afterResponseSuccess(afterResponseSuccessFn) {
      self.__afterResponseSuccess = afterResponseSuccessFn;
    },
    beforeError(beforeErrorFn) {
      self.__beforeError = beforeErrorFn;
    },
    afterError(afterErrorFn) {
      self.__afterError = afterErrorFn;
    },
    finally(finallyFn) {
      self.__finally = finallyFn;
    },
    onSuccess(successFn) {
      self.__onSuccess = successFn;
    },
    onError(errorFn) {
      self.__onError = errorFn;
    },
    unbindBeforeResponseSuccess() {
      self.__beforeResponseSuccess = self.__defaultFn;
    },
    unbindAfterResponseSuccess() {
      self.__afterResponseSuccess = self.__defaultFn;
    },
    unbindBeforeError() {
      self.__beforeError = self.__defaultFn;
    },
    unbindAfterError() {
      self.__afterError = self.__defaultFn;
    },
    unbindFinally() {
      self.__finally = self.__defaultFn;
    },
    unbindOnSuccess() {
      self.__onSuccess = self.__defaultFn;
    },
    unbindOnError() {
      self.__onError = self.__defaultFn;
    },
    unbindLifeCycleHooks() {
      const defaultLifeCycleFn = res => res;
      self.beforeResponseSuccess(defaultLifeCycleFn);
      self.afterResponseSuccess(defaultLifeCycleFn);
      self.beforeError(defaultLifeCycleFn);
      self.afterError(defaultLifeCycleFn);
      self.finally(defaultLifeCycleFn);
    }
  };
});

/*
 *	Notification
 *
 *	A model for a Notification
 */

const Notification = types.model('Notification', {
  message: types.optional(types.string, 'Unknown error'),
  context: types.maybeNull(types.enumeration(['good', 'bad', 'warning'])),
  // in ms
  timeout: types.optional(types.number, 5000)
});

/*
 *	NotificationHandler
 *
 *	A model that is in charge of displaying system messages such as, but not limited to,  error or success messages
 */

const NotificationHandler = types.compose('NotificationHandler', createCollection('Notification', Notification), types.model({}).views(self => ({
  filterContext(context) {
    return self.filterItems(item => item.context === context);
  },
  get errors() {
    return self.filterContext('bad');
  },
  get warnings() {
    return self.filterContext('warning');
  },
  get successes() {
    return self.filterContext('good');
  },
  get notifications() {
    return self.collection;
  }
})).actions(self => ({
  addNotification(notification) {
    self.prependItem(notification);
  },
  dismissNotification() {
    self.dequeueItem();
  },
  clearNotifications() {
    self.clearItems();
  }
})));

/**
 * SeatMetaInfo Model
 * Models what meta information should look like for a seat in a venue
 *
 * @typedef SeatMetaInfo information about the venue that aids in filtering
 * @property {Boolean} [available=false] whether or not this is available
 * @property {Boolean} [companion=false] whether or not this is a companion seat
 * @property {Boolean} [handicapped=false] whether or not this is a handicapped seat
 * @property {Boolean} [locked=false] whether or not this is locked a locked seat
 * @property {Boolean} [recommended=false] whether or not this seat is recommended to user
 * @property {Boolean} [reserved=false] whether or not this seat is reserved
 */
const SeatMetaInfo = types.model('SeatMetaInfo', {
  available: types.optional(types.boolean, false),
  companion: types.optional(types.boolean, false),
  handicapped: types.optional(types.boolean, false),
  recommended: types.optional(types.boolean, false),
  reserved: types.optional(types.boolean, false)
}).actions(self => ({
  /**
   * Resets to defaults
   */
  reset() {
    self.available = false;
    self.companion = false;
    self.handicapped = false;
    self.recommended = false;
    self.reserved = false;
  }
}));

/**
 * Seat Model
 * A model of what a piece of seat data should look like
 *
 * @typedef VenueSeat
 * @property {String} Key identifier for the seat
 * @property {import('../SeatMetaInfo').SeatMetaInfo} [meta]
 * @property {String} [photo] the photo for a seat
 * @property {String} [color] the color of a seat
 */
const Seat = types.model('Seat', {
  Key: types.identifier,
  meta: SeatMetaInfo,
  photo: types.maybe(types.string),
  Color: types.maybe(types.string)
}).preProcessSnapshot(snapshot => ({
  ...snapshot,
  Key: snapshot.seatKey || snapshot.Key,
  photo: (snapshot.photo || {}).photoUrl || snapshot.photo,
  meta: {
    ...(snapshot.meta || {})
  }
})).views(self => ({
  get section() {
    return self.Key.split('_')[0];
  },
  get row() {
    return self.Key.split('_')[1];
  },
  get seat() {
    return self.Key.split('_')[2];
  }
})).actions(self => ({
  init({
    meta,
    color
  }) {
    this.updateMeta(meta);
    this.setColor(color);
  },
  updateMeta(meta) {
    self.meta = {
      ...self.meta,
      ...meta
    };
  },
  /**
   * Resets the meta information to its default state
   */
  resetMeta() {
    self.meta.reset();
  },
  setColor(color) {
    self.Color = color;
  }
}));

/**
 * SectionMetaInfo Model
 * Models what meta information should look like for a section in a venue
 * @typedef SectionMetaInfo information about the venue that aids in filtering
 * @property {Boolean} [assignedSeating=true] whether or not this has assigned seats.  This is assumed true by
 * @property {Boolean} [available=false] whether or not this is available
 * @property {Boolean} [companion=false] whether or not this contains companion seats
 * @property {Boolean} [handicapped=false] whether or not this contains handicapped seats
 * @property {Boolean} [locked=false] whether or not this is locked or contains locked seats
 * @property {Boolean} [recommended=false] whether or not this is recommended to the user
 * @property {Boolean} [reserved=false] whether or not this is reserved or contains reserved seats
 * @property {Boolean} [onSale=true] whether or not this section is on sale
 * @property {Boolean} [soldOut=true] whether or not this section was sold out
 */

const SectionMetaInfo = types.compose('SectionMetaInfo', SeatMetaInfo, types.model({
  soldOut: types.optional(types.boolean, false),
  onSale: types.optional(types.boolean, true),
  assignedSeating: types.optional(types.boolean, true)
}).actions(self => ({
  /**
   * Resets to defaults
   */
  reset() {
    self.assignedSeating = true;
    self.available = false;
    self.companion = false;
    self.handicapped = false;
    self.recommended = false;
    self.reserved = false;
  }
})));

const PriceRange$1 = types.model('PriceRange', {
  low: types.optional(types.number, 0),
  high: types.optional(types.number, 0)
});

/**
 * @model
 * @typedef VenueSection A section in a venue
 * @property {String} Key identifier for the section
 * @property {Seat[]} [seats = []] a collection of seats
 * @property {import('../SectionMetaInfo').SectionMetaInfo} [meta] meta information about the section
 * @property {String} [Color] the Color of the section
 * @property {Number} [remainingSeats] the number of remaining seats in a section
 * @property {Object} [priceRange] an object containing the lowest and highest price in the section
 * @property {Number} [priceRange.low = 0] the lowest price in the section
 * @property {Number} [priceRange.high = 0] the highest price in the section
 */

const Section = types.model('Section', {
  Key: types.identifier,
  seats: types.array(Seat),
  meta: SectionMetaInfo,
  Color: types.maybe(types.string),
  remainingSeats: types.maybe(types.number),
  priceRange: PriceRange$1
}).preProcessSnapshot(snapshot => {
  return {
    ...snapshot,
    Key: snapshot.sectionKey || snapshot.Key,
    priceRange: snapshot.priceRange || {},
    meta: {
      ...(snapshot.meta || {})
    }
  };
}).views(self => ({
  get handicappedSeats() {
    return self.seats.filter(seat => seat.meta.handicapped);
  },
  get recommendedSeats() {
    return self.seats.filter(seat => seat.meta.recommended);
  },
  get reservedSeats() {
    return self.seats.filter(seat => seat.meta.reserved);
  },
  get lockedSeats() {
    return self.seats.filter(seat => seat.meta.locked);
  },
  findSeat(toFind) {
    return self.seats.find(({
      Key
    }) => Key === toFind);
  }
})).actions(self => ({
  /**
   * Initializes the section model after it has already been created
   * This will reset the meta data before updating with the new meta
   * @param {Object} section normalized data
   * @param {VenueSeatCollection | *[]} [section.seats] a collection of seats
   * @param {import('../SectionMetaInfo').SectionMetaInfo} [section.meta = {}] meta information about the section
   */
  init({
    seats,
    meta
  }) {
    this.initSeats(seats);
    this.resetMeta();
    this.updateMeta(meta);
  },
  initSeats(seats) {
    self.seats.replace(seats);
  },
  /**
   *
   * @param {import('../Seat').VenueSeat[]} seats an array of VenueSeats
   */
  addSeats(seats) {
    self.seats.concat(seats);
  },
  /**
   * @param {Boolean} assignedSeating whether or not this section contains reserved seating
   */
  setSeated(assignedSeating) {
    self.meta.assignedSeating = assignedSeating;
  },
  /**
   * Updates the meta information for a section
   * @param {import('../SectionMetaInfo').SectionMetaInfo} [meta = {}]
   */
  updateMeta(meta = {}) {
    self.meta = {
      ...self.meta,
      ...meta
    };
  },
  /**
   * Updates the price range model
   * @param {Object} [priceRange = {}]
   * @param {Number} [priceRange.low]
   * @param {Number} [priceRange.high]
   */
  updatePriceRange(priceRange = {}) {
    self.priceRange = {
      ...self.priceRange,
      ...priceRange
    };
  },
  /**
   * Resets the meta information to its default state
   */
  resetMeta() {
    self.meta.reset();
  }
}));

/**
 * Venue Model
 * Models the data for a venue
 * @model
 * @typedef VenueModel
 * @property {String} name the name of the venue
 * @property {VenueSectionCollection} sections a Collection of Sections
 */
const Venue = types.model('Venue', {
  // your properties here
  name: types.maybe(types.string),
  sections: types.array(Section)
}).views(self => ({
  findSection(toFind) {
    return self.sections.find(({
      Key
    }) => Key === toFind);
  }
})).actions(self => ({
  init({
    name = self.name,
    sections
  }) {
    self.name = name;
    self.sections.replace(sections);
  },
  deleteSection(section) {
    self.sections.remove(section);
  }
}));

/**
 * SeatSelection Model
 * Manages the state of seat selection
 */
const SeatSelection = types.model('SeatSelection', {
  venue: Venue,
  focusedSection: types.maybe(types.reference(Section)),
  selections: types.map(types.map(types.reference(Seat))),
  recommendedSelections: types.map(types.map(types.reference(Seat)))
}).preProcessSnapshot((snapshot = {}) => ({
  ...snapshot,
  selections: snapshot.selections || {}
})).views(self => ({
  parseSeatKey(seatKey) {
    const [section, row, seat] = seatKey.split('_');
    return {
      section,
      row,
      seat
    };
  },
  findSeat(key) {
    const {
      section
    } = self.parseSeatKey(key);
    return self.venue.findSection(section).findSeat(key);
  },
  sectionIsSelected(sectionKey) {
    return self.selections.has(sectionKey);
  }
})).actions(self => ({
  /**
   * Initialize a seat selection from data or  from scratch
   * @param {Object} seatSelection normalized data
   * @param {import('../Venue').VenueModel} [seatSelection.venue] a collection of seats
   * @param {String} [seatSelection.focusedSection] a key that is a reference to a Section
   * @param {Object} [seatSelection.selections] a map of Section and seat keys
   */
  init({
    venue,
    focusedSection,
    selections
  }) {
    self.setVenue(venue);
    self.focusSection(focusedSection);
    self.setSelections(selections);
  },
  setVenue(venue = {}) {
    self.venue = venue;
  },
  focusSection(section) {
    self.focusedSection = section;
  },
  setSelections(selections) {
    self.clearSelections();
    self.updateSelections(selections);
  },
  clearSelections() {
    self.selections.clear();
  },
  updateSelections(selections) {
    self.selections.merge(selections);
  },
  /**
   * @todo handle duplicate sections
   * @param {import('../Section').VenueSection[]} sections
   */
  addSections(sections) {
    self.venue.sections.concat(sections);
  },
  /**
   * Adds seats to a particular section.  If the section does not exist, it will create a new one
   * @param {String} sectionKey the key of the Section to find
   * @param {import('../Seat').VenueSeat[]} seats an array of VenueSeats to add to the section
   */
  addSeats(sectionKey, seats) {
    const section = self.venue.findSection(sectionKey);
    if (!section) {
      self.addSections([{
        key: sectionKey,
        seats
      }]);
    } else {
      section.addSeats(seats);
    }
  },
  /**
   * @callback SeatSelectionValidator decides whether changes to a seat should be accepted before they are applied
   * @param {import('../SeatMetaInfo').SeatMetaInfo} seatMeta the meta info to decide whether or not it should be selected
   * @returns {Boolean} returns true if the seat should be selected, otherwise return false
   *
   * @callback SeatSelectionHandler allows the developer to react to the seat being selected
   * @param {import('../Seat').VenueSeat} seat the seat that was just selected
   *
   * @param {String} key the key of the seat to be selected
   * @param {SeatSelectionValidator} shouldAcceptSelectedSeat
   * @param {SeatSelectionHandler} [onSeatSelected]
   */
  selectSeat(key, shouldAcceptSelectedSeat = getEnv(self).shouldAcceptSelectedSeat, onSeatSelected = getEnv(self).onSeatSelected) {
    try {
      if (typeof shouldAcceptSelectedSeat !== 'function') {
        throw Error(`Please provide the 'shouldAcceptSelectedSeat'\nYou provided the value ${shouldAcceptSelectedSeat}`);
      }
      const seat = self.findSeat(key);
      const {
        section
      } = self.parseSeatKey(key);
      if (shouldAcceptSelectedSeat(seat.meta)) {
        self.selections.merge({
          [section]: {
            [key]: key
          }
        });
        if (typeof onSeatSelected === 'function') {
          onSeatSelected(seat);
        }
      }
    } catch (e) {
      console.error(e);
    }
  },
  deselectSeat(key) {
    const {
      section
    } = self.parseSeatKey(key);
    const sectionSelections = self.selections.get(section);
    sectionSelections.delete(key);

    // clean up
    // @note sections that do not have assigned seating should still be selectable.  This clean up operation is only intended to be used with seats in a section
    if (sectionSelections.size === 0) {
      self.selections.delete(section);
    }
  }
}));

/**
 * PublishingOptions Model
 * Represents publishing options as specified by the ticket socket api
 */
const PublishingOptions = types.model('PublishingOptions', {
  HideFromList: types.boolean,
  OnSale: types.boolean,
  IsPublished: types.boolean
}).preProcessSnapshot((snapshot = {}) => {
  const {
    HideFromList = false,
    OnSale = false,
    IsPublished = false
  } = snapshot;
  return {
    HideFromList,
    OnSale,
    IsPublished
  };
});

/**
 * Photo Model
 * Represents a photo as specified by the ticket socket api
 */
const Photo = types.model('Photo', {
  ThumbUrl: types.maybe(types.string),
  PhotoUrl: types.maybe(types.string),
  PhotoId: types.maybe(types.string)
}).preProcessSnapshot((snapshot = {}) => {
  const {
    ThumbUrl,
    PhotoUrl,
    PhotoId
  } = snapshot;
  return {
    PhotoId,
    PhotoUrl,
    ThumbUrl: ThumbUrl || PhotoUrl
  };
}).views(self => ({
  get id() {
    return self.PhotoId;
  }
}));

/**
 * IsoDate Model
 * Parses iso dates for human readability
 */
const IsoDate = types.model('IsoDate', {
  raw: types.refinement(types.string, str => isValid(parseISO(str)))
}).preProcessSnapshot((snapshot = {}) => {
  let corrected = snapshot;
  if (typeof snapshot === 'string') {
    corrected = {
      raw: snapshot
    };
  }
  return corrected;
}).actions(self => ({
  setDate(date) {
    self.raw = date;
  }
})).views(self => ({
  get asDate() {
    return parseISO(self.raw);
  },
  localeDateString(locale = undefined, options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }) {
    return this.asDate.toLocaleDateString(locale, options);
  },
  lightFormat(format = 'M/d/yyyy') {
    return lightFormat(this.asDate, format);
  }
}));

/**
 * DateRange Model
 * Represents a date range as specified by the ticket socket api
 * @todo create a date format with helper methods
 */
const DateRange = types.model('DateRange', {
  From: IsoDate,
  To: IsoDate
}).preProcessSnapshot((snapshot = {}) => {
  const {
    From,
    To
  } = snapshot;
  let correctedFrom = From;
  let correctedTo = To;
  if (From && typeof From === 'string') {
    correctedFrom = {
      raw: From
    };
  }
  if (To && typeof To === 'string') {
    correctedTo = {
      raw: To
    };
  }
  return {
    ...snapshot,
    From: correctedFrom,
    To: correctedTo
  };
}).actions(self => ({
  setRange({
    From = self.From,
    To = self.To
  }) {
    self.From.setDate(From);
    self.To.setDate(To);
  }
})).views(self => ({
  get range() {
    return {
      from: self.From.asDate,
      to: self.To.asDate
    };
  },
  format({
    locale = 'en-US',
    options
  }) {
    const From = self.From.localeDateString({
      locale,
      options
    });
    const To = self.To.localeDateString({
      locale,
      options
    });
    return From === To ? From : `${From} - ${To}`;
  }
}));

/**
 * Price Model
 * Helps with the formatting and interaction with currency data
 */
const Price = types.model('Price', {
  value: types.number
}).preProcessSnapshot((snapshot = {}) => {
  return {
    value: (typeof snapshot === 'number' ? snapshot : snapshot.value) || 0
  };
}).views(self => ({
  formatLocale(locale = undefined, options = {
    style: 'currency'
  }) {
    // if the number can be expressed as 12.00, truncate to 12
    const minimumFractionDigits = self.hasDecimals ? 2 : 0;
    return self.value.toLocaleString(locale, {
      minimumFractionDigits,
      style: 'currency',
      ...options
    });
  },
  get hasDecimals() {
    return self.value % 1 !== 0;
  }
}));

/**
 * PriceRange Model
 * Aids in the formatting of 2 price models
 */
const PriceRange = types.model('PriceRange', {
  From: Price,
  To: Price
}).views(self => ({
  format({
    locale = 'en-US',
    options
  }) {
    const From = self.From.formatLocale(locale, options);
    const To = self.To.formatLocale(locale, options);
    return From === To ? From : `${From} - ${To}`;
  }
}));

/**
 * Address Model
 * Represents an address as specified by the ticket socket api
 *
 * @todo figure out what values are needed for international addresses
 */
const Address = types.model('Address', {
  CountryCode: types.maybeNull(types.string),
  PostalCode: types.maybeNull(types.string),
  State: types.maybeNull(types.string),
  City: types.maybeNull(types.string),
  Line1: types.maybeNull(types.string),
  Line2: types.maybeNull(types.string)
}).preProcessSnapshot((snapshot = {}) => {
  const {
    CountryCode = '',
    PostalCode = '',
    State = '',
    City = '',
    Line1 = '',
    Line2 = ''
  } = snapshot;
  return {
    CountryCode,
    PostalCode,
    State,
    City,
    Line1,
    Line2
  };
}).actions(self => ({
  updateAddress: ({
    CountryCode = self.CountryCode,
    PostalCode = self.PostalCode,
    State = self.State,
    City = self.City,
    Line1 = self.Line1,
    Line2 = self.Line2
  }) => {
    self.CountryCode = CountryCode;
    self.PostalCode = PostalCode;
    self.State = State;
    self.City = City;
    self.Line1 = Line1;
    self.Line2 = Line2;
  }
}));

/**
 * VenueInfo Model
 * Represents information for a venue as specified by the ticket socket api
 */
const VenueInfo = types.model('VenueInfo', {
  Address: types.maybe(types.string),
  Description: types.maybe(types.string),
  Seated: types.boolean,
  SeatingChart: types.maybe(types.string),
  ShowOnMap: types.boolean
}).preProcessSnapshot((snapshot = {}) => ({
  ...snapshot,
  Address: snapshot.Address || '',
  Seated: snapshot.Seated || false,
  ShowOnMap: snapshot.ShowOnMap || false
})).views(self => ({
  get baseVenueUrl() {
    if (!self.SeatingChart) return '';
    const fragments = self.SeatingChart.split('/');
    return fragments.slice(0, fragments.length - 1).join('/');
  }
}));

/**
 * EventDescription Model
 * Short and long descriptions for an event usually dynamically loaded from a url
 * @todo might want to give this model a loading volatile state
 */
const EventDescription = types.model('EventDescription', {
  ShortDescription: types.maybeNull(types.string),
  LongDescription: types.maybeNull(types.string)
}).preProcessSnapshot((snapshot = {}) => ({
  ...snapshot
})).actions(self => ({
  setDescriptions({
    ShortDescription = null,
    LongDescription = null
  }) {
    self.ShortDescription = ShortDescription;
    self.LongDescription = LongDescription;
  }
}));

/**
 * Event Model
 * Represents an event retrieved from the ticket socket api
 */
const Event = types.model('Event', {
  // your properties here
  PublishingOptions,
  Id: types.identifier,
  HighPrice: Price,
  LowPrice: Price,
  PriceRange: PriceRange,
  Venue: VenueInfo,
  When: DateRange,
  Sales: DateRange,
  Title: types.string,
  // optional and nullable properties
  HideICS: types.maybe(types.boolean),
  ShowCheckoutModal: types.maybe(types.boolean),
  HideSectionPhotos: types.maybe(types.boolean),
  IsRecurringEvent: types.maybe(types.boolean),
  IsSeated: types.maybe(types.boolean),
  DisplayOrder: types.maybe(types.number),
  SiteId: types.maybe(types.string),
  BaseUrl: types.maybe(types.string),
  UrlSlug: types.maybeNull(types.string),
  EmbeddedVideo: types.maybeNull(types.string),
  ListPhoto: types.maybeNull(Photo),
  DescriptionUrl: types.maybeNull(types.string),
  Description: types.maybeNull(EventDescription),
  Photos: types.maybeNull(types.array(Photo))
}).preProcessSnapshot((snapshot = {}) => {
  // handle inconsistencies between returns.
  /** @todo unify this when api is consistent */
  const {
    ListPhoto,
    IsRecurring,
    IsRecurringEvent,
    HighPrice,
    LowPrice,
    Photo
  } = snapshot;
  return {
    ...snapshot,
    ListPhoto: ListPhoto || Photo || {},
    IsRecurringEvent: IsRecurringEvent !== undefined ? IsRecurringEvent : IsRecurring,
    PriceRange: {
      From: LowPrice,
      To: HighPrice
    }
  };
});

/**
 * MinimalId Model
 *
 */
const MinimalId = types.model('MinimalId', {
  Id: types.string,
  Name: types.string
});

/**
 * Fee Model
 * Represents a fee as specified by the ticket socket api
 */
const Fee = types.model('Fee', {
  Amount: types.number,
  ExcludeFeeFromTaxCalculation: types.boolean,
  IsTax: types.boolean,
  Name: types.string
});

/**
 * PromoCode Model
 * Represents a promo code as specified by the ticket socket api
 */
const PromoCode = types.model('PromoCode', {
  Amount: Price,
  Code: types.string,
  Id: types.maybe(types.string),
  Name: types.maybe(types.string)
});

/**
 * Ticket Model
 * Represents a ticket as specified by the ticket socket api
 * @todo: might want to create 3 types of ticket: BaseTicket, TicketSelection, CartTicket
 */
const Ticket = types.model('Ticket', {
  Id: types.identifier,
  Price: Price,
  OriginalPrice: types.maybe(Price),
  Event: types.maybe(MinimalId),
  Fees: types.maybeNull(types.array(Fee)),
  PromoCodes: types.maybeNull(types.array(types.maybeNull(PromoCode))),
  Seat: types.maybeNull(types.string),
  Status: types.maybe(types.string),
  Tier: types.maybe(MinimalId),
  Type: types.maybe(MinimalId),
  Title: types.maybeNull(types.string),
  Subtitle: types.maybeNull(types.string),
  /** @todo  figure out shape of Category */
  Category: types.maybeNull(types.frozen({})),
  /** @todo  separate this into it's own model */
  Quantity: 0
}).views(self => ({
  get parsedSeatKey() {
    if (!self.Seat) return {};
    const [section, row, seat] = self.Seat.split('-');
    return {
      section,
      row,
      seat
    };
  }
})).actions(self => ({
  setQuantity(quantity) {
    self.Quantity = quantity;
  }
}));

/**
 * PurchaserInfo Model
 * Represents purchaser info as specified by the ticket socket api
 */
const PurchaserInfo = types.model('PurchaserInfo', {
  EmailAddress: '',
  LastName: '',
  FirstName: ''
}).preProcessSnapshot((snapshot = {
  EmailAddress: '',
  LastName: '',
  FirstName: ''
}) => ({
  ...snapshot
})).actions(self => ({
  setEmail(email) {
    return self.EmailAddress = email;
  },
  setFirstName(first) {
    return self.FirstName = first;
  },
  setLastName(last) {
    return self.LastName = last;
  },
  setPurchaser({
    EmailAddress,
    FirstName,
    LastName
  }) {
    self.setEmail(EmailAddress);
    self.setFirstName(FirstName);
    self.setLastName(LastName);
  },
  clear() {
    self.EmailAddress = '';
    self.LastName = '';
    self.FirstName = '';
    return true;
  }
})).views(self => ({
  get fullname() {
    return `${self.FirstName} ${self.LastName}`;
  },
  get fields() {
    return getSnapshot(self);
  }
}));

/**
 * Cart Model
 * Represents a cart as specified by the TicketSocket API
 * @todo anotate types for Ticket, Fee, and Purchaser Info
 * @typedef {Object} CartType a cart shaped defined by the TicketSocket API
 * @property {Number} [TicketTotal] sum of the costs of tickets
 * @property {Number} [TotalFeesAndTaxes] sum of all fees and taxes
 * @property {Number} [GrandTotal] sum of all tickets, fees, and taxes
 * @property {Number} [ValidUntil] timestamp in ms of how long the Cart is valid
 * @property {*[]} [Tickets = []] Tickets that have been added to the cart
 * @property {*[]} [Fees = []] Array of fees associated with the current order
 * @property {*[]} [PromoCodes = []] Array of promo codes applied to the cart
 * @property {Object} [Purchaser = {}] An object containing Purchaser information
 * @property {String} [Id] A unique identifier for the cart
 */
const Cart = types.model('Cart', {
  TicketTotal: Price,
  TotalFeesAndTaxes: Price,
  GrandTotal: Price,
  ValidUntil: types.number,
  Number: types.maybeNull(types.string),
  PromoCodes: types.maybeNull(types.array(types.maybeNull(PromoCode))),
  Tickets: createCollection('Tickets', Ticket),
  Fees: createCollection('Fees', Fee),
  Purchaser: PurchaserInfo,
  Id: types.string
}).preProcessSnapshot((snapshot = {}) => {
  const {
    TicketTotal = 0,
    TotalFeesAndTaxes = 0,
    GrandTotal = 0,
    ValidUntil = 0,
    Tickets = [],
    Fees = [],
    Purchaser = {},
    Id = ''
  } = snapshot;
  return {
    ...snapshot,
    TicketTotal,
    TotalFeesAndTaxes,
    GrandTotal,
    ValidUntil,
    Tickets,
    Fees,
    Purchaser,
    Id
  };
}).actions(self => ({
  /**
   * Initializes the cart from an API or storage
   * @param {CartType} data
   * @returns on success, the data used to hydrate the store.  on error, will just console error
   */
  hydrate(data) {
    try {
      Object.keys(data).forEach(prop => self[prop] = data[prop]);
      return data;
    } catch (e) {
      console.error(e);
    }
  },
  /**
   * Clears the cart and resets to initial values
   */
  clear() {
    self.TicketTotal = 0;
    self.TotalFeesAndTaxes = 0;
    self.GrandTotal = 0;
    self.ValidUntil = 0;
    self.Number = null;
    self.Tickets.clearItems();
    self.Fees.clearItems();
    self.Purchaser.clear();
    self.Id = '';
  }
}));

/**
 * CacheControl Model
 * A cache model built on top of lru the cache is volatile and will not be persisted across page refreshes.
 * Mainly used to prevent subsequent api requests from being called again within a certain time period.
 * To persist data, use localstorage instead of the cache.
 *
 * For instructions on how to use LRU, visit https://github.com/isaacs/node-lru-cache#readme
 */
const CacheControl = types.model('CacheControl', {
  /** The number of entries allowed in the cache */
  max: 500,
  /** By default, a cache entry will expire in 10 minutes */
  maxAge: 1000 * 60 * 10
}).preProcessSnapshot((snapshot = {}) => snapshot).volatile(self => ({
  lru: new LRU({
    max: self.max,
    maxAge: self.maxAge
  })
}))
/** exposing LRU methods here so we can track updates to the cache via snapshots since the cache is volatile */.actions(self => ({
  set(...args) {
    self.lru.set(...args);
  },
  del(key) {
    self.lru.del(key);
  },
  reset() {
    self.lru.reset();
  },
  load(entries) {
    self.lru.load(entries);
  },
  prune() {
    self.lru.prune();
  }
})).views(self => ({
  get(key) {
    return self.lru.get(key);
  },
  has(key) {
    return self.lru.has(key);
  },
  peek(key) {
    return self.lru.peek(key);
  },
  dump() {
    return self.lru.dump();
  },
  get values() {
    return self.lru.values();
  },
  get length() {
    return self.lru.length;
  },
  get itemCount() {
    return self.lru.itemCount;
  }
}));

/**
 * AsyncApi Model
 * A generic model for async functionality using api resources
 *
 * @callback APIResource
 * @param { Object } args arguments passed to an API url
 * @param { Object } [params = {}] query parameters
 * @param { Object } [headers = {}] optional headers to pass to the call
 * @returns an Axios instance
 */
const AsyncApi = types.model('AsyncApi', {
  cache: CacheControl
}).volatile(self => ({
  loading: false,
  apiError: false,
  returnData: undefined
})).actions(self => ({
  /**
   * factory function that creates an API flow
   * @param { APIResource } apiResource
   */
  createAsyncApi(apiResource, maxAge = self.cache.maxAge) {
    return flow$1(function* ({
      args = {},
      params = {},
      headers = {}
    }, options = {}) {
      /**
       * Some defaults
       */
      const {
        transformResponse = res => res,
        transformErrorResponse = err => err,
        onSuccess = res => res,
        onError = err => err,
        onFinished = res => res,
        maxAge: maxAgeOpts = maxAge
      } = options;
      self.loading = true;
      self.apiError = false;

      /**
       * Main Flow (success) --
       *
       * Step 1:  		Get a cache key based on the function being called and the arguments passed to it
       * Step 2:  		Attempt to get a value from the cache using that key
       * Step 3a:			If there is a value in the cache, DO NOT make the API call
       * Step 3b.1:   If the value in the cache is undefined, make the API call
       * Step 3b.2:		If the call is successful, set the value in the cache using the key from step 1
       * Step 3b.3:		Call transformResponse on the response data
       * Step 4:			Call onSuccess with the data
       * Step 5:			Call onFinished with the data
       * Step 6:			Clean up local state
       *
       * Main Flow (failure) --
       * If any errors occure between step 1 and 4, the error will be caught and the following will occur
       * 1)  Call transformErrorResponse
       * 2)  Set returnData to the transformed error
       * 3)  Finally block will return the error back to the caller
       */
      try {
        // create a key for the cache based off the resource being called and the args, params, and headers
        const key = `${apiResource.name}.${md5(JSON.stringify(args))}.${md5(JSON.stringify(params))}.${md5(JSON.stringify(headers))}`;
        // if this api call was made with the same arguments, returnData should contain the value
        self.returnData = self.cache.get(key);
        // only call the api if it hasn't been cached yet
        if (self.returnData === undefined) {
          // make the initial call
          const response = yield apiResource(args, params, headers);
          self.returnData = transformResponse(response);
          self.cache.set(key, self.returnData, maxAgeOpts);
        }
        if (isPromise(onSuccess)) {
          yield onSuccess(self.returnData);
        } else {
          onSuccess(self.returnData);
        }
        return self.returnData;
      } catch (error) {
        self.returnData = transformErrorResponse(error.response);
        self.apiError = true;
        // if (isPromise(onSuccess)) {
        // 	yield onError(self.returnData);
        // } else {
        // 	onError(self.returnData);
        // }

        // throw new Error(error);
      } finally {
        const res = self.returnData;
        onFinished(self.returnData);
        self.loading = false;
        self.returnData = undefined;
        return res;
      }
    });
  },
  reset() {
    self.loading = false;
    self.apiError = false;
    self.returnData = undefined;
    self.cache.reset();
  }
}));
const isPromise = toTest => toTest.then && typeof toTest.then === 'function';

/**
 * Input Model
 * Standard data interface for an input.  Should be used with a Form model instead of
 * by itself for the best developer experience
 */
const Input = types.model('Input', {
  name: types.string,
  /** @todo add more enumerations */
  type: types.optional(types.enumeration(['text', 'email', 'password']), 'text'),
  allowSubmit: false,
  required: false,
  defaultValue: '',
  value: '',
  valid: true,
  error: types.maybeNull(types.string)
}).preProcessSnapshot(snapshot => ({
  ...snapshot,
  value: snapshot.defaultValue || snapshot.value
})).volatile(self => ({
  validator: () => !(self.required && self.value === '')
})).actions(self => ({
  updateValue(value) {
    return self.value = value;
  },
  /**
   * @callback InputValidator
   * @param {String} [value]  the value of the input to be validated
   * @param {Boolean} [required] whether or not this input was configured as required
   * @returns {Boolean} whether or not the input is valid
   *
   * @param {InputValidator} validator
   * @returns {Boolean} whether or not the input is valid
   */
  validate(validator = self.validator) {
    if (validator(self.value, self.required)) {
      return self.valid = true;
    }
    return self.valid = false;
  },
  setRequired(required = true) {
    return self.required = required;
  },
  setError(error) {
    return self.error = error;
  },
  clear() {
    self.value = '';
    self.error = null;
  },
  reset() {
    self.value = self.defaultValue;
    self.error = null;
  },
  /*
   * @param {InputValidator} validator
   * @returns {Boolean} whether or not the input is valid
   */
  setValidator(validator) {
    self.validator = validator;
  }
}));

/**
 * Form Model
 * Standard interface for handling a form with user inputs
 */
const Form = types.model('Form', {
  name: types.string,
  inputs: types.map(Input)
}).volatile(self => ({
  validators: values(self.inputs).reduce((validators, input) => ({
    ...validators,
    [input.name]: input.validator
  }), {})
})).views(self => ({
  get values() {
    return values(self.inputs).reduce((vals, input) => ({
      ...vals,
      [input.name]: input.value
    }), {});
  },
  get(name) {
    return self.inputs.get(name);
  }
})).actions(self => ({
  getValidations(validators = self.validators) {
    return values(self.inputs).reduce((validations, input) => ({
      ...validations,
      [input.name]: input.validate(validators[input.name])
    }), {});
  },
  setValidator(name, validator) {
    try {
      if (!self.inputs.has(name)) {
        throw `setValidator error: No input found with name '${name}'`;
      }
      if (typeof validator !== 'function') {
        throw 'setValidator error: validator must be a function';
      }
      self.validators[name] = validator;
    } catch (e) {
      console.error(e);
    }
  },
  reset() {
    values(self.inputs).forEach(input => input.reset());
  },
  clear() {
    values(self.inputs).forEach(input => input.clear());
  }
}));

/**
 * Collection2 Model
 * V2 of collection model
 *
 * dynamically creates a collection of any supported MST type
 *
 * @todo remove the 2 once Collection is fully deprecated
 */

/**
 * @param {*} type an MST type
 */
const makeCollection2 = type => types.model({
  record: types.map(type)
}).preProcessSnapshot((snapshot = {}) => ({
  ...snapshot
})).views(self => ({
  get mappings() {
    return self.record.toJSON();
  },
  get list() {
    return Object.keys(self.mappings).map(key => self.mappings[key]);
  },
  get size() {
    return self.record.size;
  },
  values() {
    return self.record.values();
  },
  has(key) {
    return self.record.has(key);
  }
})).actions(self => ({
  /**
   * @callback RecorderCb
   * @param {*} item the entity to be recorded
   * @param {function} set a function to set the item
   */

  /**
   * adds items sequentially.  Use this for more fine grained control when adding items to the record
   * @param {*[]} items an array of items to record
   * @param {RecorderCb} recorder
   */
  save(items, recorder) {
    items.forEach(item => recorder(item, self.add));
  },
  /**
   * Merges a collection object with the record
   * @param {Object} items the object to merge with the record
   */
  merge(items) {
    self.record.merge(items);
  },
  /**
   * Completely overwrites current entries with the passed items
   * @param {Object} items the object to replace the record
   */
  replace(items) {
    self.record.replace(items);
  },
  add(key, item) {
    self.record.set(key, item);
  },
  delete(key) {
    self.record.delete(key);
  },
  clear() {
    self.record.clear();
  },
  get(key) {
    return self.record.get(key);
  }
}));

/**
 * @note
 * this might look silly... and it kind of is.  But the reason why this is being done
 * is because any model with an identifier must be unique in the ENTIRE tree.
 * Making a Ticket-like Model allows us to interact with the data as if it were an actual ticket
 * but also allows us to cache it in a Collection later on without any issues.
 */
const TicketCopy = types.compose(types.model({}), Ticket).named('TicketCopy');
/**
 * Seat2 Model
 * V2 of seat model
 */
const Seat2 = types.model('Seat2', {
  Accessible: false,
  Available: false,
  Tiers: types.array(TicketCopy),
  Key: types.string,
  Number: types.string
}).preProcessSnapshot((snapshot = {}) => ({
  ...snapshot,
  // re-mapping these temporarily, because the response isn't completely what is expected
  Key: snapshot.Seat,
  Number: snapshot.Key
}));

const SeatCollection = makeCollection2(Seat2).named('Seat');
const mapSeatsToCollection = (seats = []) => {
  const collection = {
    record: seats.reduce((map, seat) => ({
      ...map,
      [seat.Seat]: seat
    }), {})
  };
  return collection;
};
/**
 * Row Model
 * Represents a row as defined by the api
 */
const Row = types.model('Row', {
  Number: types.string,
  Seats: SeatCollection
}).preProcessSnapshot((snapshot = {}) => ({
  ...snapshot,
  Number: snapshot.Id,
  Seats: Array.isArray(snapshot.Seats) ? mapSeatsToCollection(snapshot.Seats) : snapshot.Seats
})).views(self => ({
  get numSeats() {
    return self.Seats.size;
  },
  get availableSeats() {
    const seats = self.Seats.values();
    const available = {};
    while (true) {
      const {
        value,
        done
      } = seats.next();
      if (done) break;
      if (value.Available) {
        available[value.Key] = value;
      }
    }
    return available;
  },
  getSeat(seatKey) {
    return self.Seats.get(seatKey);
  },
  hasSeat(seatKey) {
    return self.Seats.has(seatKey);
  }
})).actions(self => ({
  addSeats(seats = []) {
    return self.Seats.save(seats, (seat, add) => add(seat.Seat, seat));
  },
  addSeat(seat) {
    return self.Seats.add(seat.Seat, seat);
  },
  clearSeats() {
    return self.Seats.clear();
  }
}));

const RowCollection = makeCollection2(Row).named('RowCollection');
const mapRowsToCollection = (rows = []) => {
  const collection = {
    record: rows.reduce((map, row) => ({
      ...map,
      [row.Id]: row
    }), {})
  };
  return collection;
};
/**
 * Section2 Model
 * V2 section model
 */
const Section2 = types.model('Section2', {
  assignedSeating: types.boolean,
  Color: types.maybe(types.string),
  Key: types.string,
  Rows: RowCollection,
  SeatsAvailable: types.maybe(types.number),
  Svg: types.maybe(types.string),
  PriceRange
}).preProcessSnapshot((snapshot = {}) => ({
  ...snapshot,
  assignedSeating: snapshot.assignedSeating || !snapshot.GA || false,
  PriceRange: {
    From: snapshot.LowPrice || 0,
    To: snapshot.HighPrice || 0
  },
  Rows: Array.isArray(snapshot.Rows) ? mapRowsToCollection(snapshot.Rows) : undefined
})).views(self => ({
  get isSoldOut() {
    return self.SeatsAvailable <= 0;
  },
  get numRows() {
    return self.Rows.size;
  },
  get availableSeats() {
    let available = {};
    const rows = self.Rows.values();
    while (true) {
      const {
        done,
        value
      } = rows.next();
      if (done) break;
      available = {
        ...available,
        ...value.availableSeats
      };
    }
    return available;
  },
  getRow(rowKey) {
    return self.Rows.get(rowKey);
  },
  getSeat(rowKey, seatKey) {
    return self.hasRow(rowKey) ? self.getRow(rowKey).getSeat(seatKey) : undefined;
  },
  hasRow(rowKey) {
    return self.Rows.has(rowKey);
  }
})).actions(self => ({
  setColor(color) {
    self.Color = color;
  },
  addRow(row) {
    self.Rows.add(row.Key, row);
  },
  addRows(rows = []) {
    // note that we are accessing Id here instead of Key like the model says.  This is because of a discrepancy in the API.
    // trying to make it consistent in the meantime when there is bandwith for the API to be updated
    self.Rows.save(rows, (row, add) => add(row.Id, row));
  },
  clearRows() {
    self.Rows.clear();
  }
}));

const SectionCollection = makeCollection2(Section2).named('SectionCollection');

/**
 * VenueOrchestrator Model
 * Orchestrates the state and appearance of seats rows and sections within a venue
 *
 * @note this will replace the SeatSelection model
 */
const VenueOrchestrator = types.model('VenueOrchestrator', {
  info: VenueInfo,
  Sections: SectionCollection,
  selectedSections: types.map(types.string),
  selectedRows: types.map(types.string),
  selectedSeats: types.map(types.string)
}).volatile(self => ({
  // we are keeping a key here instead of having an MST reference because venues maybe destroyed
  // frequently and might end up with null references
  focusedRowKey: undefined,
  focusedSeatKey: undefined,
  focusedSectionKey: undefined
})).views(self => ({
  get focusedSeat() {
    return self.getSeat(self.focusedSectionKey, self.focusedRowKey, self.focusedSeatKey);
  },
  get focusedSection() {
    return self.getSection(self.focusedSectionKey);
  },
  get numSections() {
    return self.Sections.size;
  },
  getRow(section, row) {
    return self.hasSection(section) ? self.getSection(section).getRow(row) : undefined;
  },
  getSeat(section, row, seat) {
    return self.sectionHasRow(section, row) ? self.getRow(section, row).getSeat(seat) : undefined;
  },
  getSection(key) {
    return self.Sections.get(key);
  },
  hasSection(key) {
    return self.Sections.has(key);
  },
  /**
   *
   * @param {*} mapFn
   * @returns {*[]}
   */
  mapSectionsToList(mapFn) {
    const list = [];
    const sections = self.Sections.values();
    while (true) {
      const {
        value,
        done
      } = sections.next();
      if (done) break;
      list.push(mapFn(value));
    }
    return list;
  },
  /**
   * the mapping is done by the developer.
   * what this does is that it goes through the entire record, lets the developer decide how
   * each item should be formatted, and reduces it into a finished map
   * @param {*} mapFn
   * @returns {Object}
   */
  mapSectionsToProps(mapFn = item => item) {
    let reduced = {};
    // values() returns an iterator, which is why we are iterating this way
    // we do this instead of turning it into a regular object in order to
    // preserve the obeservable nature of the object for use during mapping
    const sections = self.Sections.values();
    while (true) {
      const {
        value,
        done
      } = sections.next();
      if (done) break;
      reduced[value.Key] = mapFn(value);
    }
    return reduced;
  },
  mapSeatsToProps(section, mapFn = item => item) {
    if (!self.hasSection(section)) return {};
    let reduced = {};
    const rows = self.getSection(section).Rows.values();
    while (true) {
      const {
        value,
        done
      } = rows.next();
      if (done) break;
      const seats = value.Seats.values();
      while (true) {
        const {
          value: seat,
          done: seatsDone
        } = seats.next();
        if (seatsDone) break;
        reduced[seat.Key] = mapFn(seat);
      }
    }
    return reduced;
  },
  sectionHasRow(section, row) {
    return self.hasSection(section) && self.getSection(section).hasRow(row);
  },
  sectionRowHasSeat(section, row, seat) {
    return self.sectionHasRow(section, row) && self.getRow(section, row).hasSeat(seat);
  },
  seatIsSelected(seat) {
    return self.selectedSeats.has(seat);
  }
})).actions(self => ({
  /**
   *
   * @param {*} section
   * @param {*} rows
   */
  addRows(section, rows = []) {
    if (!self.hasSection(section)) {
      self.addSection({
        Key: section
      });
    }
    return self.getSection(section).addRows(rows);
  },
  addSections(sections = []) {
    self.Sections.save(sections, (section, add) => add(section.Key, section));
  },
  addSection(section) {
    self.Sections.add(section.Key, section);
  },
  clearSections() {
    self.Sections.clear();
  },
  clearSelected() {
    self.clearSelectedSeats();
    self.clearSelectedRows();
    self.clearSelectedSections();
  },
  clearSelectedSections() {
    self.selectedSections.clear();
  },
  clearSelectedSeats() {
    self.selectedSeats.clear();
  },
  clearSelectedRows() {
    self.selectedRows.clear();
  },
  deselectSeat(seat) {
    self.selectedSeats.delete(seat);
  },
  selectSection(section) {
    self.selectedSections.set(section, section);
  },
  selectSeat(seat) {
    self.selectedSeats.set(seat, seat);
  },
  selectRow(row) {
    self.selectedRows.set(row, row);
  },
  setFocusedSectionKey(section) {
    self.focusedSectionKey = section;
  },
  setFocusedSeatKey(section, row, seat) {
    self.focusedSectionKey = section;
    self.focusedRowKey = row;
    self.focusedSeatKey = seat;
  },
  toggleSelectedSeat(seat) {
    if (self.selectedSeats.has(seat)) {
      self.deselectSeat(seat);
      return undefined;
    }
    self.selectSeat(seat);
    return seat;
  },
  unfocusSectionKey() {
    self.focusedSectionKey = undefined;
  },
  unfocusSeatKey() {
    self.focusedSeatKey = undefined;
  }
}));

/**
 * PaymentProcess Model
 * Represents a payment processor as specified by the ts api
 */
const PaymentProcess = types.model({
  UseSandbox: false,
  AccountId: types.maybeNull(types.string),
  TestKey: types.maybeNull(types.string),
  LiveKey: types.maybeNull(types.string),
  Name: types.maybeNull(types.string)
}).preProcessSnapshot((snapshot = {}) => ({
  ...snapshot
})).views(self => ({
  get locationId() {
    return self.AccountId;
  },
  get applicationId() {
    return self.UseSandbox ? self.TestKey : self.LiveKey;
  },
  get isReady() {
    return (self.TestKey || self.LiveKey) && self.Name;
  }
})).actions(self => ({
  updatePaymentProcessInfo({
    UseSandbox,
    TestKey,
    LiveKey,
    Name,
    AccountId
  }) {
    self.UseSandbox = UseSandbox;
    self.TestKey = TestKey;
    self.LiveKey = LiveKey;
    self.Name = Name;
    self.AccountId = AccountId;
  }
}));

/**
 * UiTicket Model
 * A model describing a ticket for display
 */
const UiTicket = types.compose(Ticket, types.model({
  loading: false,
  errors: false
})).named('Ticket').actions(self => ({
  setLoading(loading = true) {
    self.loading = loading;
  },
  setErrors(e) {
    self.errors = e;
  }
}));

/**
 * Timer Model
 * Convenience model for starting stopping a timer and displaying the result
 */

// easy conversions from milliseconds
const ONE_SECOND = 1000;
const ONE_MINUTE = ONE_SECOND * 60;

/**
 * @note
 * for the sake of simplicity and also more accuracy, this timer will tick every second
 * if that becomes a performance issue, we can lengthen the inteval to be every minute
 * the reason why we don't do that now is because it requires us to use setTimeout instead
 * of setInterval.  This means that after every minute we need to cancel the timeout, clean it up,
 * and start a new timeout.  Additionally, once we reach the threshold that we want to we need to
 * decrease the interval.  Problems arise at this change of interval, because sometimes it does not
 * occur straign at 60 seconds.  For example, if we made it change at 5 minutes, there would be a
 * possibility that on the updated tick, the next displayed time would be 4:42 instead of 4:59 because
 * of how math works (meaning correct computations).  This introduces a weird corner case where we would
 * need to stop it a minute before we would want the interval to change to every second, but still show
 * it in minutes (ie "6 min remaining") until it reaches our threshold ("5 min remaining") which would then
 * change to ("4:59 remaining").  This is absolutely doable, but it complicates this relatively simple code
 * and may not even bring that much performance benefits.  That's why I have opted to keep it implemented
 * as is:
 *
 * - The Official Joseph Caluza™
 */
const Timer = types.model({
  // number of milliseconds the cart timer should be active
  duration: 0,
  // the timer to be cleared
  timerId: types.maybe(types.number)
}).named('Timer').volatile(self => ({
  initialized: false,
  interval: ONE_SECOND
})).views(self => ({
  get isExpired() {
    return self.duration <= 0;
  },
  get timeConversions() {
    const totalMilliseconds = self.duration;
    // don't want to divide by 0
    const totalSeconds = self.isExpired ? 0 : self.duration / ONE_SECOND;
    const totalMinutes = self.isExpired ? 0 : self.duration / ONE_MINUTE;
    return {
      totalMilliseconds,
      totalSeconds,
      totalMinutes
    };
  },
  get timeRemaining() {
    const {
      totalMinutes: minutes,
      totalSeconds,
      totalMilliseconds
    } = self.timeConversions;
    // if there is more than one minute in milliseconds
    // 		seconds remaining is the total milliseconds mod one minute
    // else
    // 		seconds remaining is the total seconds
    const seconds = (totalMilliseconds > ONE_MINUTE ? totalMilliseconds % ONE_MINUTE : totalSeconds) / 1000;
    return {
      minutes,
      seconds
    };
  }
})).actions(self => ({
  startTimer(duration, onTick) {
    if (!self.initialized) {
      self.duration = duration;
      const timerId = setInterval(() => self.tick(onTick), self.interval);
      self.timerId = timerId;
      self.initialized = true;
    }
  },
  tick(onTick = () => undefined) {
    self.decreaseDuration();
    if (self.isExpired) self.cancelTimer();
    onTick(self.duration, self.isExpired);
  },
  decreaseDuration() {
    self.duration -= self.interval;
  },
  cancelTimer() {
    clearInterval(self.timerId);
    self.initialized = false;
    self.timerId = undefined;
    self.duration = 0;
  }
}));

const SUPPORTED_LOCALES = ['en-us', 'ja-jp', 'ko-kr', 'fr-ca'];

/**
 * LanguageLocale Model
 * Stores information about the site language codes
 */
const LanguageLocale = types.model({
  Locale: types.maybe(types.refinement(types.string, lang => SUPPORTED_LOCALES.indexOf(lang.toLowerCase()) > -1)),
  DefaultLanguageSelection: types.maybe(types.string)
}).named('LanguageLocale');

/**
 * CurrencyLocale Model
 * Represents a site currency code info
 */
const CurrencyLocale = types.model({
  Code: types.maybe(types.string),
  Symbol: types.maybe(types.string)
}).named('CurrencyLocale');

/**
 * Localization Model
 * Stores info needed for i18n and l10n
 */
const Localization = types.model({
  Currency: types.maybe(CurrencyLocale),
  Language: types.maybe(LanguageLocale)
}).named('Localization').views(self => ({
  get priceDisplay() {
    return {
      locale: self.Language.Locale,
      options: {
        style: 'currency',
        currency: self.Currency.Code
      }
    };
  },
  getPriceDisplayWithDigits(minimumFractionDigits = 2) {
    return {
      locale: self.Language.Locale,
      options: {
        style: 'currency',
        currency: self.Currency.Code,
        minimumFractionDigits
      }
    };
  }
}));

/**
 * LegacyAppearanceSettings Model
 * Represents site appearance based on the ts api
 * @note will be deprecated in the future
 */
const LegacyAppearanceSettings = types.model({
  LinkColor: types.maybeNull(types.string),
  AccentColor: types.maybeNull(types.string)
}).named('LegacyAppearanceSettings').views(self => ({
  get brandColor() {
    return self.LinkColor;
  },
  get accentColor() {
    return self.AccentColor;
  }
}));

const SiteInfo = types.model({
  Name: '',
  FavIcon: types.maybeNull(Photo),
  Localization: types.maybe(Localization),
  Logo: types.maybeNull(Photo),
  Categories: types.maybeNull(types.array(types.map(types.string))),
  Venues: types.maybeNull(types.array(VenueInfo)),
  CheckoutWidget: types.maybeNull(types.array(LegacyAppearanceSettings)),
  PolicySettings: types.maybeNull(types.map(types.maybeNull(types.string)))
}).named('SiteInfo').volatile(self => ({
  initialized: false
})).views(self => ({
  get json() {
    return self.toJSON();
  },
  get appearanceSettings() {
    return (self.CheckoutWidget || [])[0];
  }
})).actions(self => ({
  __applySettings({
    CheckoutWidget,
    PolicySettings
  }) {
    self.CheckoutWidget = CheckoutWidget;
    self.PolicySettings = PolicySettings;
  },
  __applyLocalization(localization) {
    self.Localization = localization;
  },
  setInfo({
    Settings,
    ...info
  }) {
    try {
      const {
        Currency,
        Locale,
        DefaultLanguageSelection
      } = Settings;
      self.__applySettings(Settings);
      self.__applyLocalization({
        Currency,
        Language: {
          Locale,
          DefaultLanguageSelection
        }
      });
      Object.keys(info).forEach(key => {
        if (!self.json.hasOwnProperty(key)) return;
        self[key] = info[key];
        return;
      });
      self.initialized = true;
    } catch (e) {
      self.initialized = false;
      throw new Error(e);
    }
  }
}));

const DEFAULT_PAGING = {
  Take: 6,
  Skip: 0,
  Total: 0
};
/**
 * Paging Model
 * Keeps track of how many items are showing and how many to get for the next page
 */
const Paging = makeCollection2(types.number).views(self => ({
  get Skip() {
    return self.record.Skip;
  },
  get Take() {
    return self.record.Take;
  },
  get Total() {
    return self.record.Total;
  }
})).actions(self => ({
  updateSkip(skip) {
    self.add('Skip', parseInt(skip));
    return skip;
  },
  updateTake(take) {
    self.add('Take', parseInt(take));
    return take;
  },
  updateTotal(total) {
    self.add('Total', parseInt(total));
    return total;
  },
  reset() {
    self.replace(DEFAULT_PAGING);
  },
  next() {
    const next = self.get('Skip') + self.get('Take');
    self.updateSkip(next);
  }
}));

/**
 * Filters Model
 * Keeps track of filter state
 *
 * @note this is an MST map which would allow us to add a filters on the fly instead of
 * adhering to hard coded ones.  However, I think it might be easier to have clearly defined filters instead of adding them dynamically.
 * It might be more beneficial to create more primitive models (like `DateRange`) and create different "filter sets" made from those primitives.
 * This would allow for more re-usability while also allowing us to create tailored filters for each use case since there is not much variance
 * in the ways we use filters in our apps
 */

/**
 * @note there may be benefit to replacing From and To with a `DateRange` model (see DateRange model).  For simplicity, I am
 * keeping them as regular strings because the use case may be a bit different even though the properties are similar
 */
const Filters = makeCollection2(types.string).actions(self => ({
  updateTitle(title) {
    self.add('Title', title);
  },
  updateFromDate(date) {
    self.add('From', date);
  },
  updateToDate(date) {
    self.add('To', date);
  },
  updateDateRange(from, to) {
    self.updateFromDate(from);
    self.updateToDate(to);
  },
  clearDates() {
    self.delete('From');
    self.delete('To');
  },
  clearTitle() {
    self.delete('Title');
  },
  clearAll() {
    self.clearDates();
    self.clearTitle();
  }
}));

// set up the cache
const EventsCache = makeCollection2(Event).named('EventsCache');

/**
 * EventCollection Model
 * Caches eventsand provides an interface to display a subset of events
 */
const EventCollection = types.model({
  EventsCache,
  Filters,
  Paging,
  EventsCollection: types.array(types.reference(Event))
}).named('EventsCollection').preProcessSnapshot((snapshot = {}) => ({
  ...snapshot,
  EventsCache: snapshot.EventsCache || {},
  Filters: snapshot.Filters || {},
  Paging: snapshot.Paging || {},
  EventsCollection: snapshot.EventsCollection || []
})).views(self => ({
  get listOfEvents() {
    return self.EventsCollection;
  },
  get listIsEmpty() {
    return self.EventsCollection.length === 0;
  },
  mapEventsById(events) {
    return events.reduce((map, event) => ({
      ...map,
      [event.Id]: event
    }), {});
  },
  findEventBySlug(slug) {
    return self.EventsCollection.find(event => event.UrlSlug === slug);
  },
  getEventByIdOrSlug(IdOrSlug) {
    return self.EventsCache.get(IdOrSlug) || self.findEventBySlug(IdOrSlug);
  }
})).actions(self => ({
  /**
   * For internal use only.  Used to record to the Event Cache
   * @deprecated
   * @param {*} event an Event to record
   * @param {function} set the function that sets the value
   */
  __recorder(event, set) {
    set(event.Id, event);
  },
  /**
   * Updates the current Event Cache
   * @param {*[]} events an array of events to update the Event Cache with
   */
  cache(events) {
    const eventMap = self.mapEventsById(events);
    self.EventsCache.merge(eventMap);
  },
  /**
   * Empties the current Event Cache
   */
  clearCache() {
    self.EventsCache.clear();
  },
  /**
   * Updates the current Event Collection
   * @param {*[]} events an array of events to update the collection with
   * @param {Boolean} [replace = false] if true, will completely replace current events in the collection with the new events
   */
  updateList(events, replace = false) {
    self.cache(events);
    const references = events.map(({
      Id
    }) => Id);
    if (replace) {
      self.EventsCollection.replace(references);
    } else {
      references.forEach(reference => self.EventsCollection.push(reference));
    }
  },
  /**
   * Empties the current Event Collection
   */
  clearList() {
    self.EventsCollection.clear();
  },
  /**
   * Updates the Paging total
   * @param {Number} total the gross amount of events
   */
  updateTotal(total) {
    self.Paging.updateTotal(total);
  },
  /**
   * Updates the title being filtered by
   * @param {String} title the title to filter by
   */
  updateTitle(title) {
    self.Filters.updateTitle(title);
  }
}));

/**
 * IcsEntityMetaData Model
 * Common meta data for an entity created with the ics api
 */
const IcsEntityMetaData = types.model({
  id: types.identifier,
  status: types.maybe(types.string),
  isArchived: types.maybe(types.boolean),
  createdAt: types.maybe(IsoDate),
  updatedAt: types.maybe(IsoDate)
}).named('IcsEntityMetaData').preProcessSnapshot(snapshot => {
  if (snapshot === null || snapshot === undefined) return null;
  const {
    createdAt,
    updatedAt
  } = snapshot;
  const correctedCreatedAt = correctDate(createdAt);
  const correctedUpdatedAt = correctDate(updatedAt);
  return {
    ...snapshot,
    id: snapshot.id === null ? snapshot.id : `${snapshot.id}`,
    createdAt: correctedCreatedAt,
    updatedAt: correctedUpdatedAt
  };
});
function correctDate(snapshotVal = {}) {
  if (typeof snapshotVal === 'string' || typeof (snapshotVal || {}).raw === 'string') {
    return snapshotVal;
  }
  return undefined;
}

/**
 * IcsUser Model
 * Ics user as represented by the ics api
 */
const IcsUser = types.compose(IcsEntityMetaData, types.model({
  email: types.maybeNull(types.string),
  // Required
  firstName: types.maybeNull(types.string),
  // Required
  lastName: types.maybeNull(types.string),
  // Required
  role: types.maybeNull(types.string),
  // Required
  business: types.maybeNull(types.number),
  // Required
  affiliateId: types.maybeNull(types.string),
  // Required (or clientId)
  affiliateName: types.maybeNull(types.string),
  // Required (or clientId)
  clientId: types.maybeNull(types.string),
  // Required (or affiliateId)
  clientName: types.maybeNull(types.string),
  // Required (or affiliateId)
  isTeamAdmin: types.maybeNull(types.boolean),
  // Required
  isSuperUser: types.maybeNull(types.boolean),
  ip: types.optional(types.maybeNull(types.array(types.string)), [])
})).named('IcsUser').preProcessSnapshot((snapshot = {}) => ({
  ...snapshot,
  affiliateId: snapshot.affiliateId === null ? snapshot.affiliateId : `${snapshot.affiliateId}`,
  clientId: snapshot.clientId === null ? snapshot.clientId : `${snapshot.clientId}`,
  ip: Array.isArray(snapshot.ip) ? snapshot.ip : typeof snapshot.ip === 'string' && snapshot.ip[0] === '{' && snapshot.ip[snapshot.length - 1] === '}' ? snapshot.ip.substr(0, snapshot.ip.length - 1).substr(1).split(',') : null
})).views(self => ({
  get name() {
    return self.firstName ? `${self.firstName} ${self.lastName}` : self.email;
  },
  get initials() {
    return `${self.firstName.charAt(0)}${self.lastName.charAt(0)}`;
  }
})).actions(self => ({
  // this is here because the company field in the API is deprecated and this is the easiest place to keep track of it for now
  setBusiness(business) {
    self.business = business;
  }
}));

/**
 * IcsCompany Model
 * A company as represented by the ics api
 */
const IcsCompany = types.compose(IcsEntityMetaData, types.model({
  clientId: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  emailAddress: types.maybeNull(types.string),
  businessAddress: types.maybeNull(types.string),
  supportEmail: types.maybeNull(types.string),
  logo: types.maybeNull(types.string),
  cardFlexSecurityKey: types.maybeNull(types.string),
  isConfigured: false,
  currency: types.maybeNull(types.string),
  language: types.maybeNull(types.string),
  eCommerceSolution: types.maybeNull(types.string),
  webstoreURL: types.maybeNull(types.string),
  maskInfo: types.maybeNull(types.boolean),
  privacyPolicyURL: types.maybeNull(types.optional(types.string, '')),
  termsAndConditionsURL: types.maybeNull(types.optional(types.string, '')),
  processorId: types.maybeNull(types.string)
})).named('IcsCompany').views(self => ({
  get isProfileComplete() {
    const {
      name,
      businessAddress,
      termsAndConditionsURL,
      privacyPolicyURL,
      currency,
      language
    } = self;
    return !!name && !!businessAddress && !!termsAndConditionsURL && !!privacyPolicyURL && !!currency && !!language;
  },
  get isEcommereceComplete() {
    const {
      eCommerceSolution,
      webstoreURL
    } = self;
    return !!eCommerceSolution && !!webstoreURL;
  },
  get isBrandComplete() {
    const {
      logo
    } = self;
    return !!logo;
  },
  get isSetupComplete() {
    return self.isProfileComplete && self.isEcommereceComplete && self.isBrandComplete;
  }
}));

// processorId: 4;

/**
 * IcsClient Model
 * Client as represented by the ics api
 */
const IcsClient = types.compose(IcsEntityMetaData, types.model({
  name: types.maybeNull(types.string),
  publicKey: types.maybeNull(types.string),
  regenKey: types.maybeNull(types.string),
  affiliateId: types.maybeNull(types.string),
  affiliateName: types.maybeNull(types.string),
  campaignCount: types.maybeNull(types.string),
  campaignSID: types.maybeNull(types.string),
  messageServiceSID: types.maybeNull(types.string),
  smsPhone: types.maybeNull(types.string),
  smsUseCaseStatus: types.maybeNull(types.enumeration(['PENDING', 'IN_PROGRESS', 'VERIFIED', 'FAILED'])),
  allowSMS: types.maybeNull(types.boolean),
  isArchived: types.maybeNull(types.boolean)
})).named('IcsClient').preProcessSnapshot((snapshot = {}) => ({
  ...snapshot,
  affiliateId: snapshot.affiliateId === null ? snapshot.affiliateId : `${snapshot.affiliateId}`
}));

/**
 * IcsPaymentGateway Model
 * Payment gateway as represented by the ics api
 */
const IcsPaymentGateway = types.compose(IcsEntityMetaData, types.model({
  authorizenetApiLoginKey: types.maybeNull(types.string),
  authorizenetTransactionKey: types.maybeNull(types.string),
  cardFlexSecurityKey: types.maybeNull(types.string),
  clientId: types.maybeNull(types.string),
  companyId: types.maybeNull(types.string),
  type: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  squareAppId: types.maybeNull(types.string),
  squareLocationId: types.maybeNull(types.string),
  squareLocationName: types.maybeNull(types.string),
  squareAppAccessToken: types.maybeNull(types.string),
  squareOAuthAccessToken: types.maybeNull(types.string),
  squareOAuthAccessTokenExpiration: types.maybeNull(types.string),
  squareOAuthRefreshToken: types.maybeNull(types.string),
  squareMerchantId: types.maybeNull(types.string),
  stripeAccessToken: types.maybeNull(types.string),
  stripeAccountId: types.maybeNull(types.string),
  stripePublishKey: types.maybeNull(types.string),
  stripeRefreshToken: types.maybeNull(types.string)
})).named('IcsPaymentGateway');

/**
 * IcsStat Model
 * A stat as represented by the ics api
 */
const IcsStat = types.model({
  date: types.maybeNull(types.string),
  totalConversions: 0,
  totalInvitesClicked: 0,
  totalInfluencers: 0,
  totalRevenue: 0,
  totalUtilizationRate: 0,
  totalUtilizedInfluencers: 0
}).named('IcsStat').preProcessSnapshot((snapshot = {}) => ({
  date: snapshot.date || null,
  totalConversions: parseInt(snapshot.totalConversions) || 0,
  totalInvitesClicked: parseInt(snapshot.totalInvitesClicked) || 0,
  totalInfluencers: parseInt(snapshot.totalInfluencers) || 0,
  totalRevenue: parseInt(snapshot.totalRevenue) || 0,
  totalUtilizationRate: parseInt(snapshot.totalUtilizationRate) || 0,
  totalUtilizedInfluencers: parseInt(snapshot.totalUtilizedInfluencers) || 0
}));

/**
 * IcsAffiliate Model
 * An affiliate as represented by the ics api
 */
const IcsAffiliate = types.compose(IcsEntityMetaData, types.model({
  name: types.maybeNull(types.string),
  parentId: types.maybeNull(types.string),
  parentName: types.maybeNull(types.string),
  clientCount: types.maybeNull(types.string),
  campaignCount: types.maybeNull(types.string)
})).named('IcsAffiliate').preProcessSnapshot((snapshot = {}) => ({
  ...snapshot,
  parentId: snapshot.parentId === null ? snapshot.parentId : `${snapshot.parentId}`
}));

/**
 * IcsCampaign Model
 * A campaign as represented by the ics api
 */
const IcsCampaign$1 = types.model({
  id: types.identifier,
  name: types.maybeNull(types.string),
  fromEmail: types.maybeNull(types.string),
  fromName: types.maybeNull(types.string),
  emailSubject: types.maybeNull(types.string),
  active: types.maybeNull(types.boolean),
  conversionGoal: types.maybeNull(types.number),
  offerHeading: types.maybeNull(types.string),
  offerInstructions: types.maybeNull(types.string),
  shareMessage: types.maybeNull(types.string),
  thankYouImage: types.maybeNull(types.string),
  redirectUrl: types.maybeNull(types.string),
  offerImage: types.maybeNull(types.string),
  userId: types.maybeNull(types.number),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
  thankYouImageMobile: types.maybeNull(types.string),
  offerImageMobile: types.maybeNull(types.string),
  shareImage: types.maybeNull(types.string),
  shareTitle: types.maybeNull(types.string),
  locale: types.optional(types.maybeNull(types.string), 'en-US'),
  offerImageCaption: types.maybeNull(types.string),
  offerImageTitle: types.maybeNull(types.string),
  shareVideo: types.maybeNull(types.string),
  clientId: types.maybeNull(types.number),
  maskInfo: types.maybeNull(types.boolean),
  isArchived: types.maybeNull(types.boolean),
  emailNote: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  termsAndConditionsURL: types.maybeNull(types.string),
  privacyPolicyURL: types.maybeNull(types.string),
  refundFullAmount: types.maybeNull(types.boolean),
  refundAmount: types.maybeNull(Price),
  useCardFlexRefund: types.maybeNull(types.boolean),
  cardFlexManualRefund: types.maybeNull(types.boolean),
  clientName: types.maybeNull(types.string),
  giftCardAmount: types.maybeNull(types.number),
  isManual: types.maybeNull(types.boolean),
  processingAmount: types.maybeNull(Price),
  processorId: types.maybeNull(types.string),
  processorType: types.maybeNull(types.string),
  discountCode: types.maybeNull(types.string),
  discountCodeDesc: types.maybeNull(types.string),
  useSMS: false,
  useSMSConsent: false,
  useEmail: false,
  useEmailConsent: false
}).named('IcsCampaign').preProcessSnapshot(({
  id,
  processorId,
  ...snap
}) => ({
  ...snap,
  id: `${id}`,
  processorId: processorId ? `${processorId}` : null
}));

/**
 * IcsCampaign Model
 * A campaign as represented by the ics api
 */
const IcsCampaign = types.model({
  id: types.identifier,
  name: types.maybeNull(types.string),
  fromEmail: types.maybeNull(types.string),
  fromName: types.maybeNull(types.string),
  emailSubject: types.maybeNull(types.string),
  active: types.maybeNull(types.boolean),
  conversionGoal: types.maybeNull(types.number),
  offerHeading: types.maybeNull(types.string),
  offerInstructions: types.maybeNull(types.string),
  shareMessage: types.maybeNull(types.string),
  thankYouImage: types.maybeNull(types.string),
  redirectUrl: types.maybeNull(types.string),
  offerImage: types.maybeNull(types.string),
  userId: types.maybeNull(types.number),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
  thankYouImageMobile: types.maybeNull(types.string),
  offerImageMobile: types.maybeNull(types.string),
  shareImage: types.maybeNull(types.string),
  shareTitle: types.maybeNull(types.string),
  locale: types.optional(types.maybeNull(types.string), 'en-US'),
  offerImageCaption: types.maybeNull(types.string),
  offerImageTitle: types.maybeNull(types.string),
  shareVideo: types.maybeNull(types.string),
  clientId: types.maybeNull(types.number),
  maskInfo: types.maybeNull(types.boolean),
  isArchived: types.maybeNull(types.boolean),
  emailNote: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  termsAndConditionsURL: types.maybeNull(types.string),
  privacyPolicyURL: types.maybeNull(types.string),
  refundFullAmount: types.maybeNull(types.boolean),
  refundAmount: types.maybeNull(Price),
  useCardFlexRefund: types.maybeNull(types.boolean),
  cardFlexManualRefund: types.maybeNull(types.boolean),
  clientName: types.maybeNull(types.string),
  giftCardAmount: types.maybeNull(types.number),
  isManual: types.maybeNull(types.boolean),
  processingAmount: types.maybeNull(Price),
  processorId: types.maybeNull(types.string),
  processorType: types.maybeNull(types.string),
  discountCode: types.maybeNull(types.string),
  discountCodeDesc: types.maybeNull(types.string),
  useEmail: false,
  useEmailConsent: false,
  useSMS: false,
  useSMSConsent: false
}).named('IcsCampaign');

/**
 * IcsInfluencer Model
 * An influencer as represented by the ics api
 */
const IcsInfluencer = types.compose(IcsEntityMetaData, types.model({
  campaignId: types.maybeNull(types.number),
  conversionCount: types.maybeNull(types.number),
  conversionRevenue: types.maybeNull(types.number),
  email: types.maybeNull(types.string),
  emailConsent: types.maybeNull(types.boolean),
  mobileNumber: types.maybeNull(types.string),
  smsConsent: types.maybeNull(types.boolean),
  goalsReached: types.maybeNull(types.number),
  goalsProcessed: types.maybeNull(types.number),
  inviteCount: types.maybeNull(types.number),
  utilized: types.maybeNull(types.number),
  name: types.maybeNull(types.string),
  orderId: types.maybeNull(types.string),
  revenue: types.maybeNull(types.number),
  invitesClicked: types.maybeNull(types.number),
  rewardsDue: types.maybeNull(types.number),
  rewardsIssued: types.maybeNull(types.number),
  inviteLink: types.maybeNull(types.string),
  inviteResendsRemaining: types.maybeNull(types.number),
  resendAfterDate: types.string,
  signupConversions: types.maybeNull(types.number),
  participationConversions: types.maybeNull(types.number),
  pageViewConversions: types.maybeNull(types.number),
  shareConversions: types.maybeNull(types.number),
  facebook: types.maybeNull(types.number),
  emailChannel: types.maybeNull(types.number),
  messenger: types.maybeNull(types.number),
  kakao: types.maybeNull(types.number),
  wechat: types.maybeNull(types.number),
  sms: types.maybeNull(types.number),
  whatsapp: types.maybeNull(types.number),
  influencerLink: types.maybeNull(types.string),
  identifier: types.maybeNull(types.string),
  transactionId: types.maybeNull(types.string),
  isV2: types.maybeNull(types.boolean)
})).named('IcsInfluencer').preProcessSnapshot(({
  influencerLink,
  transactionId,
  ...snap
}) => ({
  ...snap,
  influencerLink: influencerLink ? `${influencerLink}` : null,
  transactionId: transactionId ? `${transactionId}` : null
})).views(self => ({
  get isProcessed() {
    const {
      goalsProcessed,
      rewardsDue,
      rewardsIssued
    } = self;
    return rewardsDue === 0 && rewardsIssued > 0 || goalsProcessed > 0;
  },
  get isEligible() {
    const {
      goalsReached,
      rewardsDue
    } = self;
    return rewardsDue > 0 || goalsReached > 0; // goals reached is for backwards compatibility.  will be phased out
  }
}));

/**
 * IcsInvite Model
 * An Invite as represented by the ICS API
 */
const IcsInvite = types.compose(IcsEntityMetaData, types.model({
  type: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  identifier: types.maybeNull(types.string),
  email: types.maybeNull(types.string),
  shareMessage: types.maybeNull(types.string),
  redirectUrl: types.maybeNull(types.string),
  convertedRevenue: types.maybeNull(Price),
  hasConversion: types.maybeNull(types.boolean),
  inviteClickCount: types.maybeNull(types.number),
  inviteResendsRemaining: types.maybeNull(types.number),
  resendAfterDate: types.maybeNull(types.string),
  influencerId: types.maybeNull(types.number),
  campaignId: types.maybeNull(types.number),
  sentCount: types.maybeNull(types.number),
  conversionCount: types.maybeNull(types.number)
})).named('IcsInvite');

const MaybeNothing = type => types.union(types.maybeNull(type), types.maybe(type));

/**
 * SquareLocation Model
 * A square location as represented by the square api
 */
const SquareLocation = types.model({
  id: MaybeNothing(types.string),
  name: MaybeNothing(types.string),
  address: MaybeNothing(types.model({
    address_line_1: MaybeNothing(types.string),
    address_line_2: MaybeNothing(types.string),
    locality: MaybeNothing(types.string),
    administrative_district_level_1: MaybeNothing(types.string),
    postal_code: MaybeNothing(types.string),
    country: MaybeNothing(types.string)
  })),
  timezone: MaybeNothing(types.string),
  capabilities: MaybeNothing(types.array(types.string)),
  status: MaybeNothing(types.string),
  created_at: MaybeNothing(types.string),
  merchant_id: MaybeNothing(types.string),
  country: MaybeNothing(types.string),
  language_code: MaybeNothing(types.string),
  currency: MaybeNothing(types.string),
  business_name: MaybeNothing(types.string),
  type: MaybeNothing(types.string),
  business_hours: MaybeNothing(types.model({})),
  coordinates: MaybeNothing(types.model({
    latitude: MaybeNothing(types.number),
    longitude: MaybeNothing(types.number)
  })),
  mcc: MaybeNothing(types.string)
}).named('SquareLocation');

/**
 * IcsConversion Model
 * A conversion as represented by the ics api
 */
const IcsConversion = types.compose(IcsEntityMetaData, types.model({
  campaignId: types.maybeNull(types.number),
  identifier: types.maybeNull(types.string),
  influencerId: types.maybeNull(types.number),
  inviteId: types.maybeNull(types.number),
  invitedInfluencerEmail: types.maybeNull(types.string),
  invitedInfluencerId: types.maybeNull(types.number),
  invitedInfluencerName: types.maybeNull(types.string),
  revenue: types.maybeNull(types.number),
  transactionGateway: types.maybeNull(types.string),
  transactionId: types.maybeNull(types.string),
  conversionType: types.maybeNull(types.string)
})).named('IcsConversion')
// .preProcessSnapshot((snap) => ({}))
.views(self => ({}));

/**
 * IcsReward Model
 * A Reward as represented by the ICS API
 */
const IcsReward = types.compose(IcsEntityMetaData, types.model({
  name: types.maybeNull(types.string),
  identifier: types.maybeNull(types.string),
  rewardType: types.maybeNull(types.string),
  influencerEmail: types.maybeNull(types.string),
  rewardAmount: types.maybeNull(Price),
  influencerId: types.maybeNull(types.number),
  campaignId: types.maybeNull(types.number)
})).named('IcsReward')
// .preProcessSnapshot((snap) => ({}))
.views(self => ({}));

export { Address, AsyncApi, CacheControl, Cart, CreateAsyncActionsFlow as CreateAsyncFlow, CurrencyLocale, DateRange, Event, EventCollection, EventDescription, Fee, Filters, Form, IcsAffiliate, IcsCampaign$1 as IcsCampaign, IcsCampaign as IcsCampaignV2, IcsClient, IcsCompany, IcsConversion, IcsEntityMetaData, IcsInfluencer, IcsInvite, IcsPaymentGateway, IcsReward, IcsStat, IcsUser, Input, IsoDate, LanguageLocale, LegacyAppearanceSettings, Localization, MinimalId, Notification, NotificationHandler, Paging, PaymentProcess, Photo, Price, PriceRange, PromoCode, PublishingOptions, PurchaserInfo, Row, Seat, Seat2, SeatMetaInfo, SeatSelection, Section, Section2, SectionMetaInfo, SiteInfo, SquareLocation, Ticket, Timer, UiTicket, Venue, VenueInfo, VenueOrchestrator, createCollection, makeCollection2 as makeCollection };
